import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import type { Permission } from '../../auth/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** When set, the active membership must hold this permission. Omit for any
   *  authenticated member (the universal `/learn/*` consumer surface). */
  requirePerm?: Permission;
  redirectTo?: string;
}

/**
 * ProtectedRoute component
 *
 * Guards a route for authenticated members. While the session is hydrating it
 * shows a spinner; an unauthenticated visitor is redirected to the login page.
 * When `requirePerm` is supplied, the active membership must hold that
 * permission or the member is sent to `/access-denied`.
 *
 * This single guard replaces both the legacy admin role guard and the separate
 * learner guard.
 *
 * @param children - The component to render when access is granted
 * @param requirePerm - Optional permission required on the active membership
 * @param redirectTo - Optional custom redirect path (defaults to /auth/login)
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requirePerm,
  redirectTo = '/auth/login',
}) => {
  const { isAuthenticated, isLoading, activeMembership } = useAuth();
  const location = useLocation();

  // Show loading spinner while the session hydrates.
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background-primary">
        <div className="spinner spinner-lg text-primary-600 dark:text-primary-350" aria-label="Loading..." />
      </div>
    );
  }

  // Redirect to login if not authenticated, preserving the attempted location.
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Permission gate: management surfaces require a specific permission.
  if (requirePerm && !(activeMembership?.perms ?? []).includes(requirePerm)) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
